import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { combineStrings } from 'assets/utils/data/String';
import { isEmail, isValidPhoneNumber } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { compact, difference, isEmpty, set } from 'lodash';
import dealerDeleteFileApi from 'models/dealerManagement/dealer/deleteFile/Api.Dealer.DeleteFile';
import dealerDeleteProductDocumentApi from 'models/dealerManagement/dealer/deleteProductDocument/Api.Dealer.DeleteProductDocument';
import dealerDeleteProductEnhancementDocumentApi from 'models/dealerManagement/dealer/deleteProductEnhancementDocument/Api.Dealer.DeleteProductEnhancementDocument';
import dealerDeleteUiFileApi from 'models/dealerManagement/dealer/deleteUiFile/Api.Dealer.DeleteUiFile';
import dealerSaveApi from 'models/dealerManagement/dealer/save/Api.Dealer.Save';
import dealerUploadFileApi from 'models/dealerManagement/dealer/uploadFile/Api.Dealer.UploadFile';
import dealerUploadProductDocumentApi from 'models/dealerManagement/dealer/uploadProductDocument/Api.Dealer.UploadProductDocument';
import dealerUploadProductEnhancementDocumentApi from 'models/dealerManagement/dealer/uploadProductEnhancementDocument/Api.Dealer.UploadProductEnhancementDocument';
import dealerUploadUiFileApi from 'models/dealerManagement/dealer/uploadUiFile/Api.Dealer.UploadUiFile';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import { useMemo, useState } from 'react';
import { useDealerProvider } from '../Dealer.Hooks';
import DealerClaimTypeAutomationRules from './edit/Dealer.ClaimTypeAutomationRules';
import DealerContractConsentProductAggregate from './edit/Dealer.ContractConsentProductAggregate';
import DealerGeneralForm from './edit/Dealer.General';
import DealerProductDocumentForm from './edit/Dealer.ProductDocumentForm';
import DealerProductEnhancementDocumentForm from './edit/Dealer.ProductEnhancementDocumentForm';
import DealerUiSettingsForm from './edit/Dealer.UiSettingsForm';

export type DealerForm = Omit<
  Api.Core.Dealer.Save.IRequest,
  'productDealerDocuments' | 'productEnhancementDealerDocuments' | 'claimTypeDealerAutomationRules'
> & {
  files: Hooks.Input.File.Info[];
  logo: Hooks.Input.File.Info;
  productDealerDocuments: Array<
    Utils.Unpacked<Api.Core.Dealer.Save.IRequest['productDealerDocuments']> & {
      formFile?: Hooks.Input.File.Info;
      isDeleted?: boolean;
      translatedDocuments: Array<
        Api.Core.DocumentTranslation.Save.IRequest & {
          isDeleted?: boolean;
        }
      >;
    }
  >;
  productEnhancementDealerDocuments: Array<
    Utils.Unpacked<Api.Core.Dealer.Save.IRequest['productEnhancementDealerDocuments']> & {
      formFile?: Hooks.Input.File.Info;
      isDeleted?: boolean;
      translatedDocuments: Array<
        Api.Core.DocumentTranslation.Save.IRequest & {
          isDeleted?: boolean;
        }
      >;
    }
  >;
  claimTypeDealerAutomationRules: Array<
    Utils.Unpacked<Api.Core.Dealer.Save.IRequest['claimTypeDealerAutomationRules']> & {
      isDeleted?: boolean;
      isUpdated?: boolean;
      claimTypeId?: number;
    }
  >;
  claimTypes: Model.IClaimType[];
};
export default function DealerEdit() {
  const { lang } = getLocales();
  const { user, isDistributor, isDealerOrDistributor, isAdministrator } = useAuthStore();
  const { selected, pageRouter, listApi, saveDealerClaimTypes, saveDealerAutomationRules } = useDealerProvider();
  const [additionalProductIds, setAdditionalProductIds] = useState<number[]>([]);
  const [additionalProductEnhancementIds, setAdditionalProductEnhancementIds] = useState<number[]>([]);
  const [additionalClaimTypeIds, setAdditionalClaimTypeIds] = useState<number[]>([]);

  const productDealerDocIds = useMemo(
    () => compact(selected?.productDealerDocuments?.map((d) => d.id)),
    [selected?.productDealerDocuments]
  );
  const listProductDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductDealerDocument', documentId: productDealerDocIds },
      wait: !productDealerDocIds.length,
    },
    [productDealerDocIds]
  );
  const enhancementDealerDocIds = useMemo(
    () => compact(selected?.productEnhancementDealerDocuments?.map((d) => d.id)),
    [selected?.productEnhancementDealerDocuments]
  );
  const listEnhancementDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductEnhancementDealerDocument', documentId: enhancementDealerDocIds },
      wait: !enhancementDealerDocIds.length,
    },
    [enhancementDealerDocIds]
  );
  return (
    <>
      <ModalForm<DealerForm>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[
          selected,
          listProductDocumentTranslationsApi?.payload,
          listEnhancementDocumentTranslationsApi?.payload,
        ]}
        title={lang.dealer}
        default={{
          ...selected?.toJSON(),
          documentLanguageIsoCode: selected?.documentLanguageIsoCode ?? Language.english,
          representativeId: selected?.representativeId ?? user?.distributorId,
          logo: selected?.uiSettingsFile
            ? {
                id: selected.uiSettingsFile.id,
                dataUrl: selected.uiSettingsFile.url,
                name: selected.uiSettingsFile.name,
                type: selected.uiSettingsFile.contentType as Utils.File.MimeType,
              }
            : null,
          files: selected?.mediaFiles?.map((it) => ({
            id: it.id,
            dataUrl: it.url,
            name: it.name,
            type: it.contentType as Utils.File.MimeType,
          })),
          productDealerDocuments: selected?.productDealerDocuments?.map((it) => ({
            ...it,
            translatedDocuments: listProductDocumentTranslationsApi.payload?.data.filter((t) => t.documentId === it.id),
          })),
          productEnhancementDealerDocuments: selected?.productEnhancementDealerDocuments?.map((it) => ({
            ...it,
            translatedDocuments: listEnhancementDocumentTranslationsApi.payload?.data.filter(
              (t) => t.documentId === it.id
            ),
          })),
        }}
        onCancel={pageRouter.updateParams}
        onSubmit={async ({
          files,
          logo,
          productDealerDocuments,
          productEnhancementDealerDocuments,
          claimTypeDealerAutomationRules,
          dealerClaimTypes,
          claimTypes,
          ...data
        }) => {
          const response = await dealerSaveApi(data);

          if (response.status === HttpStatus.ok) {
            let totalFiles = 0;
            let uploadedFiles = 0;
            let deletedFiles = 0;
            let totalDeletedFiles = 0;
            const deletedFileIds = difference(
              selected?.mediaFiles?.map((it) => it.id),
              files?.map((it) => it.id)
            );
            for (const fileId of deletedFileIds || []) {
              const fileUploadResponse = await dealerDeleteFileApi(response.payload || data.id, {
                fileId,
              });
              if (fileUploadResponse.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            }
            for (const formFile of files?.filter((it) => !it.id && it.original) || []) {
              const fileUploadResponse = await dealerUploadFileApi(response.payload || data.id, {
                formFile: formFile.original,
              });
              if (fileUploadResponse.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }
            for (const productDealerDoc of productDealerDocuments || []) {
              let documentId: null | number = productDealerDoc.id;
              if (productDealerDoc.isDeleted && productDealerDoc.id) {
                const docDeleteReponse = await dealerDeleteProductDocumentApi({
                  id: response.payload || data.id,
                  productDealerDocumentId: productDealerDoc.id,
                });
                if (docDeleteReponse.status === HttpStatus.ok) {
                  deletedFiles += 1;
                }
                totalDeletedFiles += 1;
              } else if (productDealerDoc.formFile) {
                const docUploadResponse = await dealerUploadProductDocumentApi(
                  {
                    id: response.payload || data.id,
                    productId: productDealerDoc.productDocument.productId,
                    productDocumentId: productDealerDoc.productDocumentId,
                  },
                  { formFile: productDealerDoc.formFile.original }
                );
                documentId = docUploadResponse?.payload?.documentId;
                if (docUploadResponse.status === HttpStatus.ok) {
                  uploadedFiles += 1;
                }
                totalFiles += 1;
              }

              for (const translatedDoc of productDealerDoc.translatedDocuments || []) {
                if ((productDealerDoc.isDeleted || translatedDoc.isDeleted) && translatedDoc.id) {
                  const docDeleteReponse = await documentTranslationDeleteApi({
                    id: translatedDoc.id,
                  });
                  if (docDeleteReponse.status === HttpStatus.ok) {
                    deletedFiles += 1;
                  }
                  totalDeletedFiles += 1;
                } else if (translatedDoc.formFile && !translatedDoc.id) {
                  const docUploadResponse = await documentTranslationSaveApi({ ...translatedDoc, documentId });
                  if (docUploadResponse.status === HttpStatus.ok) {
                    uploadedFiles += 1;
                  }
                  totalFiles += 1;
                }
              }
            }
            for (const productEnhancementDealerDoc of productEnhancementDealerDocuments || []) {
              let documentId: null | number = productEnhancementDealerDoc.id;
              if (productEnhancementDealerDoc.isDeleted && productEnhancementDealerDoc.id) {
                const docDeleteReponse = await dealerDeleteProductEnhancementDocumentApi({
                  id: response.payload || data.id,
                  productEnhancementDealerDocumentId: productEnhancementDealerDoc.id,
                });
                if (docDeleteReponse.status === HttpStatus.ok) {
                  deletedFiles += 1;
                }
                totalDeletedFiles += 1;
              } else if (productEnhancementDealerDoc.formFile) {
                const docUploadResponse = await dealerUploadProductEnhancementDocumentApi(
                  {
                    id: response.payload || data.id,
                    productEnhancementId: productEnhancementDealerDoc.productEnhancementDocument.productEnhancementId,
                    productEnhancementDocumentId: productEnhancementDealerDoc.productEnhancementDocumentId,
                  },
                  { formFile: productEnhancementDealerDoc.formFile.original }
                );
                documentId = docUploadResponse?.payload?.documentId;
                if (docUploadResponse.status === HttpStatus.ok) {
                  uploadedFiles += 1;
                }
                totalFiles += 1;

                for (const translatedDoc of productEnhancementDealerDoc.translatedDocuments || []) {
                  if ((productEnhancementDealerDoc.isDeleted || translatedDoc.isDeleted) && translatedDoc.id) {
                    const docDeleteReponse = await documentTranslationDeleteApi({
                      id: translatedDoc.id,
                    });
                    if (docDeleteReponse.status === HttpStatus.ok) {
                      deletedFiles += 1;
                    }
                    totalDeletedFiles += 1;
                  } else if (translatedDoc.formFile && !translatedDoc.id) {
                    const docUploadResponse = await documentTranslationSaveApi({ ...translatedDoc, documentId });
                    if (docUploadResponse.status === HttpStatus.ok) {
                      uploadedFiles += 1;
                    }
                    totalFiles += 1;
                  }
                }
              }
            }
            if (logo?.id !== selected?.uiSettingsFile?.id) {
              const logoUploadResponse = await dealerDeleteUiFileApi(response.payload || data.id);
              if (logoUploadResponse.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            }
            if (logo && !logo?.id) {
              const logoUploadResponse = await dealerUploadUiFileApi(response.payload || data.id, {
                formFile: logo.original,
              });
              if (logoUploadResponse.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }

            await saveDealerClaimTypes(dealerClaimTypes, response.payload);
            await saveDealerAutomationRules(
              claimTypeDealerAutomationRules,
              dealerClaimTypes,
              claimTypes,
              response.payload
            );

            if (uploadedFiles !== totalFiles || deletedFiles !== totalDeletedFiles) {
              addLog({
                warning: combineStrings(
                  ' ',
                  lang.formSuccessAndFilesFailedMessage,
                  totalFiles > 0 ? `${uploadedFiles}/${totalFiles} ${lang.filesUploaded}` : null,
                  totalDeletedFiles > 0 ? `${deletedFiles}/${totalDeletedFiles} ${lang.filesGotDeleted}!` : null
                ),
              });
            } else {
              addLog({ success: lang.saveSuccess });
            }
          } else {
            addLog({ error: response.message });
          }
          listApi.execute((body) => body);
          pageRouter.updateParams({});
        }}
        validation={(data, errors) => {
          if (!data.name) errors.name = lang.mustNotBeEmpty;
          if (!data.streetAddress) errors.streetAddress = lang.mustNotBeEmpty;
          if (!data.provinceCode) errors.provinceCode = lang.mustNotBeEmpty;
          if (!data.city) errors.city = lang.mustNotBeEmpty;
          if (!data.countryCode) errors.countryCode = lang.mustNotBeEmpty;
          if (!data.postalCode) errors.postalCode = lang.mustNotBeEmpty;
          if (!data.representativeId) errors.representativeId = lang.mustNotBeEmpty;
          if (!data.providerId) errors.providerId = lang.mustNotBeEmpty;
          if (data.isFederalTaxExempt && !data.federalTaxRegistrationNumber?.trim())
            set(errors, 'federalTaxRegistrationNumber', lang.federalTaxRegistrationRequiredForExemption);
          if (data.isProvincialTaxExempt && !data.provincialTaxRegistrationNumber?.trim())
            set(errors, 'provincialTaxRegistrationNumber', lang.provincialTaxRegistrationRequiredForExemption);
          if (data.primaryPhoneNumber && !isValidPhoneNumber(data.primaryPhoneNumber))
            set(errors, 'primaryPhoneNumber', lang.invalidFormatRequires.replace('{format}', '###-###-####'));
          if (data.primaryEmailAddress && !isEmail(data.primaryEmailAddress))
            set(errors, 'primaryEmailAddress', lang.invalidEmailFormatRequires);
          if (data.claimPaymentEmail && !isEmail(data.claimPaymentEmail))
            set(errors, 'claimPaymentEmail', lang.invalidEmailFormatRequires);
          if (!isEmpty(data.accountingEmails) && data.accountingEmails.some((it) => !isEmail(it)))
            set(errors, 'accountingEmails', lang.invalidEmailFormatRequires);
          if (data.claimTypeDealerAutomationRules?.some((rule) => !rule.isDeleted && !rule.effects?.length)) {
            set(errors, 'claimTypeDealerAutomationRules._objectError', lang.required);
          }
        }}
        tabs={[
          ...(isDistributor || isDealerOrDistributor || isAdministrator
            ? [{ id: 'info', icon: 'fas-info-circle', text: lang.info }]
            : []),
          ...(isDealerOrDistributor || isAdministrator
            ? [{ id: 'productDocuments', icon: 'fas-file', text: lang.productDocs }]
            : []),
          ...(isDealerOrDistributor || isAdministrator
            ? [{ id: 'productEnhancementDocuments', icon: 'fas-file-alt', text: lang.enhDocs }]
            : []),
          ...(isDealerOrDistributor || isAdministrator
            ? [{ id: 'automationRules', icon: 'fas-file-alt', text: lang.automationRules }]
            : []),
          ...(isDistributor || isDealerOrDistributor || isAdministrator
            ? [
                {
                  id: 'contractConsentProductAggregates',
                  icon: 'fas-database',
                  text: lang.consentProductAgg,
                },
              ]
            : []),
          ...(isDistributor || isDealerOrDistributor || isAdministrator
            ? [{ id: 'uiSettings', icon: 'fas-paint-roller', text: lang.uiSettings }]
            : []),
        ]}
        render={({ activeTab }) =>
          activeTab?.id === 'productDocuments' ? (
            <DealerProductDocumentForm
              additionalProductIds={additionalProductIds}
              setAdditionalProductIds={setAdditionalProductIds}
            />
          ) : activeTab?.id === 'productEnhancementDocuments' ? (
            <DealerProductEnhancementDocumentForm
              additionalProductEnhancementIds={additionalProductEnhancementIds}
              setAdditionalProductEnhancementIds={setAdditionalProductEnhancementIds}
            />
          ) : activeTab?.id === 'uiSettings' ? (
            <DealerUiSettingsForm />
          ) : activeTab?.id === 'info' || !activeTab?.id ? (
            <DealerGeneralForm
              additionalProductIds={additionalProductIds}
              setAdditionalProductIds={setAdditionalProductIds}
              additionalProductEnhancementIds={additionalProductEnhancementIds}
              setAdditionalProductEnhancementIds={setAdditionalProductEnhancementIds}
              additionalClaimTypeIds={additionalClaimTypeIds}
              setAdditionalClaimTypeIds={setAdditionalClaimTypeIds}
            />
          ) : activeTab?.id === 'contractConsentProductAggregates' ? (
            <DealerContractConsentProductAggregate />
          ) : activeTab?.id === 'automationRules' ? (
            <DealerClaimTypeAutomationRules additionalClaimTypeIds={additionalClaimTypeIds} />
          ) : null
        }
      />
    </>
  );
}
